import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { BsArrowRight, BsCheckCircleFill } from "react-icons/bs";
import { scanQrCode } from "services/qrCodes/indes";
import { errorResponse } from "config/config";
import "./QrCode.scss";

const initialForm = {
  firstname: "",
  lastname: "",
  email: "",
  mobile: "",
};

function QrCode() {
  const { qrcode: qrcodeFromParams } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const qrcode = qrcodeFromParams || location.state?.qrcode;

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isAlreadyUtilized, setIsAlreadyUtilized] = useState(false);

  useEffect(() => {
    if (qrcodeFromParams) {
      navigate("/qr-details", {
        state: { qrcode: qrcodeFromParams },
        replace: true,
      });
    }
  }, [qrcodeFromParams, navigate]);

  const handleChange = ({ target: { name, value } }) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};

    if (!form.firstname.trim()) newErrors.firstname = "First name is required";
    if (!form.lastname.trim()) newErrors.lastname = "Last name is required";

    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      newErrors.email = "Enter a valid email address";
    }

    if (!form.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
    } else if (!/^[6-9]\d{9}$/.test(form.mobile.trim())) {
      newErrors.mobile = "Enter a valid 10-digit mobile number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");

    if (!validate()) return;

    try {
      setLoading(true);

      const res = await scanQrCode({
        qrcode,
        firstname: form.firstname.trim(),
        lastname: form.lastname.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim(),
      });

      if (res.code === 200) {
        // Check if QR code is already utilized
        if (res.message && res.message.toLowerCase().includes("already been utilized")) {
          setIsAlreadyUtilized(true);
          setSuccessMessage(res.message || "This QR code has already been utilized.");
          setSuccess(true);
        } else {
          setIsAlreadyUtilized(false);
          setSuccessMessage(res.message || "QR Code scanned successfully");
          setSuccess(true);
        }
      } else {
        setApiError(res.message || "Something went wrong. Please try again.");
      }
    } catch (err) {
      errorResponse(err);
      setApiError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const confettiPieces = Array.from({ length: 40 });

  if (qrcodeFromParams) {
    return null;
  }

  if (!qrcode) {
    return (
      <section className="qrscan_section">
        <div className="qrscan_wrap">
          <div className="qrscan_card">
            <div className="qrscan_header">
              <span className="badge">QR Check-in</span>
              <h1>Invalid or expired link</h1>
              <p>Please scan the QR code again to continue.</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="qrscan_section">
      <div className="qrscan_wrap">
        {!success ? (
          <div className="qrscan_card">
            <div className="qrscan_header">
              <span className="badge">QR Check-in</span>
              <h1>Confirm your details</h1>
            </div>

            {apiError && (
              <div className="alert_error" role="alert">
                {apiError}
              </div>
            )}

            <form className="qrscan_form" onSubmit={handleSubmit} noValidate>
              <div className="form_row">
                <div className={`input_group ${errors.firstname ? "has_error" : ""}`}>
                  <label htmlFor="firstname">First name</label>
                  <input
                    id="firstname"
                    name="firstname"
                    type="text"
                    placeholder="First Name..."
                    value={form.firstname}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  {errors.firstname && (
                    <span className="field_error">{errors.firstname}</span>
                  )}
                </div>

                <div className={`input_group ${errors.lastname ? "has_error" : ""}`}>
                  <label htmlFor="lastname">Last name</label>
                  <input
                    id="lastname"
                    name="lastname"
                    type="text"
                    placeholder="Last Name..."
                    value={form.lastname}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  {errors.lastname && (
                    <span className="field_error">{errors.lastname}</span>
                  )}
                </div>
              </div>

              <div className={`input_group ${errors.email ? "has_error" : ""}`}>
                <label htmlFor="email">Email address</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Email..."
                  value={form.email}
                  onChange={handleChange}
                  disabled={loading}
                />
                {errors.email && <span className="field_error">{errors.email}</span>}
              </div>

              <div className={`input_group ${errors.mobile ? "has_error" : ""}`}>
                <label htmlFor="mobile">Mobile number</label>
                <input
                  id="mobile"
                  name="mobile"
                  type="tel"
                  maxLength={10}
                  placeholder="Mobile No..."
                  value={form.mobile}
                  onChange={handleChange}
                  disabled={loading}
                />
                {errors.mobile && <span className="field_error">{errors.mobile}</span>}
              </div>

              <button type="submit" className="button" disabled={loading}>
                {loading ? (
                  <span className="button_loader" aria-label="Submitting" />
                ) : (
                  <>
                    Continue
                    <span className="icon">
                      <BsArrowRight />
                    </span>
                  </>
                )}
              </button>
            </form>
          </div>
        ) : (
          <div className={`qrscan_success ${isAlreadyUtilized ? 'already_utilized' : ''}`}>
            {!isAlreadyUtilized && (
              <div className="confetti_wrap" aria-hidden="true">
                {confettiPieces.map((_, i) => (
                  <span key={i} className={`confetti piece_${i % 8}`} />
                ))}
              </div>
            )}

            <div className={`success_icon ${isAlreadyUtilized ? 'info_icon' : ''}`}>
              {isAlreadyUtilized ? 'ℹ️' : <BsCheckCircleFill />}
            </div>

            <h2>
              {isAlreadyUtilized ? 'Already Utilized' : '🎉 Congratulations!'}
            </h2>
            <p>{successMessage}</p>

            <button className="button" onClick={() => navigate("/")}>
              Back to home
              <span className="icon">
                <BsArrowRight />
              </span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

export default QrCode;